import {
  ScheduleOutlined,
  SettingOutlined,
  MailOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import {
  useAuth,
  useFeatureEntitlements,
  usePermissionEntitlements,
} from '@frontegg/react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';
import { AmbientMblHoverIcon, AmbientMblIcon, CloseIcon } from 'src/icons';
import { getFromStorage } from 'src/lib/storage';
import { setMobileRecord } from 'src/modules/ambient_solo/store/slices/encounter';
import { Text } from '../baseComponents';
import { InvitePractitioner, InviteUser } from './inviteUser';
import { ProTrialCountDown } from './proTrialCountdown';
import SideNavbarLogo from './SideNavbarLogo';
import { Popover, Divider, Badge } from 'antd';
import DownloadChromeExtensionSideBanner from '../../modules/ChromeExtensionSideBanner/DownloadChromeExtensionSideBanner';
import { useAppNavMenus } from 'src/pages';
import { analytics } from 'src/services';
import { useIsOP } from 'src/hooks/misc';
import classNames from 'classnames';
import { RippleDot } from '../rippleDot';
// TODO: fix useeffect dependencies and setstate methods
const SubMenuPopover = ({ submenu, title }) => {
  return (
    <div className="submenu-popover">
      <div className="submenu-header">
        <span>{title}</span>
      </div>
      {submenu && <Divider className="submenu-divider" />}
      {submenu?.map((subItem, subIndex) => (
        <NavLink
          key={subIndex}
          to={subItem.path}
          className="submenu-popover-item block hover:bg-gray-100"
        >
          <div className="flex items-center gap-2">
            {subItem.icon}
            <span>{subItem.title}</span>
          </div>
        </NavLink>
      ))}
    </div>
  );
};

const WithBadge = ({ type, children }) => {
  const jsx = <div className="flex items-center gap-2">{children}</div>;
  if (type === 'dot') {
    return (
      <Badge
        offset={[5, 2]}
        count={<RippleDot size="sm" appearance="alternate" />}
      >
        {jsx}
      </Badge>
    );
  }
  return jsx;
};

export const SideNavbar = ({
  showSidebar,
  handleToggleSidebar,
  isMinimized,
  setIsMinimized,
}) => {
  const { isEntitled: hasIntakeAccess } = useFeatureEntitlements('intake');
  const { isEntitled: hasFollowupAccess } = useFeatureEntitlements('followup');
  const { isEntitled: hasReferralsAccess } = useFeatureEntitlements('referral');
  const { isEntitled: hasAmbientAccess } = useFeatureEntitlements('ambient');
  // const { isEntitled: hasChromeExtensionLinkAccess } = useFeatureEntitlements(
  //   'link-to-chrome-extension'
  // );

  const hasChromeExtensionLinkAccess = false;
  const { isEntitled: isPhoneTriageModuleEntitled } =
    useFeatureEntitlements('phone-triage');
  const { isEntitled: hasCallReadAccess } =
    usePermissionEntitlements('calls.read');
  const { isMobileRecord } = useSelector((state) => state?.encounterState);
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const location = useLocation();
  const [isLoading, setLoading] = useState(true);
  const [isSchedulePageActive, setSchedulePageActive] = useState(false);
  const [isExpanding, setIsExpanding] = useState(false);
  const dispatch = useDispatch();
  const { user } = useAuth();
  const isOP = useIsOP();
  const handleMenuMouseEnter = (title) => {
    setHoveredMenu(title);
  };

  const handleMenuMouseLeave = () => {
    setHoveredMenu(null);
  };

  const handleMenuClick = (title) => {
    if (typeof title === 'string') {
      // eslint-disable-next-line no-undef
      analytics.track(
        `Clicked ${title === 'Get $1000' ? 'Get Referral' : title} Nav Menu`,
        { email: user?.email }
      );
    } else if (typeof title === 'object') {
      let modified_title = 'Template Exchange';
      // eslint-disable-next-line no-undef
      analytics.track(`Clicked ${modified_title} Nav Menu`, {
        email: user?.email,
      });
    }
    if (title === 'Care AI') {
      window.open(import.meta.env.VITE_CARE_AI_UI_BASE_URL, '_blank');
    } else {
      setActiveMenu(title);
      localStorage.setItem('activeMenu', title);
    }
  };

  const nullSelectedRecord = () => {
    dispatch(setMobileRecord(false));
  };

  useEffect(() => {
    const initialMenu = menus.find((menu) => {
      // Check main menu path
      if (location.pathname === menu.path) return true;

      // Check submenu paths if they exist
      if (menu.submenu) {
        return menu.submenu.some(
          (subItem) => location.pathname === subItem.path
        );
      }

      return false;
    });

    setSchedulePageActive(false);
    if (initialMenu) {
      setActiveMenu(initialMenu.title);
      localStorage.setItem('activeMenu', initialMenu.title); // Save initial active menu to local storage
    } else if (/\/schedule/.test(location.pathname)) {
      setActiveMenu('Appointments');
      setSchedulePageActive(true);
      localStorage.setItem('activeMenu', 'Appointments');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const { settingsOrgValue } = useSelector((state) => state?.appointmentState);
  const { currentPractitionerEntitlements } = useSelector(
    (state) => state?.practitionerState
  );
  const isAdmin = settingsOrgValue?.roles?.some((role) =>
    ['admin', 'superadmin'].includes(role)
  );

  const isAuraPro = currentPractitionerEntitlements?.plans?.some(
    (plan) => plan?.metadata?.type === 'pro'
  );
  const isAuraTeams = currentPractitionerEntitlements?.plans?.some(
    (plan) => plan?.metadata?.type === 'teams'
  );
  const isPLGUser = currentPractitionerEntitlements?.plans?.some(
    (plan) => plan?.metadata?.type === 'plg'
  );
  const showProTrialCountDown = !isAuraPro && isPLGUser;

  const { isEntitled: isInviteUserEntitled } =
    useFeatureEntitlements('invite-user');
  const hasInviteUserAccess = isInviteUserEntitled && isAdmin;

  const menus = useAppNavMenus({ activeMenu });

  useEffect(() => {
    const storedActiveMenu = localStorage.getItem('activeMenu');
    if (storedActiveMenu) {
      setActiveMenu(storedActiveMenu);
    }
    setLoading(false);
  }, []);

  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );

  const showInviteUserWithMirrorOption =
    hasIntakeAccess ||
    hasFollowupAccess ||
    isAuraPro ||
    isAuraTeams ||
    isPhoneTriageModuleEntitled;

  const isMobile = window.innerWidth <= 1260;
  const mobile_encounter_id = getFromStorage(
    `${currentPractitioner?.org_uuid}_topbar_encounter_id`
  );
  const [showEncounterId, setShowEncounterId] = useState(false);

  useEffect(() => {
    setShowEncounterId(true);
  }, [mobile_encounter_id]);

  return (
    <>
      {isMobile &&
      !isMobileRecord &&
      showEncounterId &&
      location.pathname !== `/mobileEncounterDetails/${mobile_encounter_id}` ? (
        <nav className="bottom-menu fixed z-[100] h-20 bottom-0 left-0 right-0 bg-white rounded-t-xl px-0">
          <ul className="flex justify-between items-center h-full">
            {hasReferralsAccess && (
              <li className="flex-grow flex-shrink-0">
                <NavLink
                  to="/referrals"
                  className="flex flex-col items-center justify-center"
                  style={{ textDecoration: 'none' }}
                >
                  <div className="menu-text text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl">
                    <MailOutlined
                      className={`${
                        location.pathname === '/referrals' ||
                        location.pathname.startsWith('/referrals/')
                          ? 'active-icon mb-2'
                          : 'mb-4'
                      }`}
                      onClick={nullSelectedRecord}
                    />
                    Inbox
                  </div>
                </NavLink>
              </li>
            )}
            {(hasIntakeAccess || hasFollowupAccess) && (
              <li className="flex-grow flex-shrink-0 ">
                <NavLink
                  to="/appointments"
                  className="flex flex-col items-center justify-center "
                  style={{ textDecoration: 'none' }}
                >
                  <div className="menu-text text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl">
                    <ScheduleOutlined
                      className={`${
                        location.pathname === '/appointments'
                          ? 'active-icon mb-2'
                          : 'mb-4'
                      }`}
                      onClick={nullSelectedRecord}
                    />
                    Appointments
                  </div>
                </NavLink>
              </li>
            )}
            {isPhoneTriageModuleEntitled && hasCallReadAccess && (
              <li className="flex-grow flex-shrink-0 ">
                <NavLink
                  to="/phone-triage/calls"
                  className="flex flex-col items-center justify-center"
                  style={{ textDecoration: 'none' }}
                >
                  <div className="menu-text text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl">
                    <PhoneOutlined
                      className={`${
                        location.pathname === '/phone-triage/calls' ||
                        location.pathname.startsWith('/phone-triage/')
                          ? 'active-icon mb-2'
                          : 'mb-4'
                      }`}
                      onClick={nullSelectedRecord}
                    />
                    Phone
                  </div>
                </NavLink>
              </li>
            )}

            {hasAmbientAccess && (
              <li className="flex-grow flex-shrink-0 ">
                <NavLink
                  to="/encounters"
                  className="flex flex-col items-center justify-center"
                  style={{ textDecoration: 'none' }}
                >
                  <div className="menu-text text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl">
                    {location.pathname.includes('/encounters') ? (
                      <div
                        className={`${
                          location.pathname === '/encounters'
                            ? 'active-icon mb-2'
                            : 'mb-4'
                        }`}
                        onClick={nullSelectedRecord}
                      >
                        <AmbientMblHoverIcon />
                      </div>
                    ) : (
                      <div
                        className={`${
                          location.pathname === '/encounters'
                            ? 'active-icon mb-2'
                            : 'mb-4'
                        }`}
                        onClick={nullSelectedRecord}
                      >
                        <AmbientMblIcon />
                      </div>
                    )}
                    {isOP ? 'Opie' : 'Aura'} AI Scribe
                  </div>
                </NavLink>
              </li>
            )}
            <li className="flex-grow flex-shrink-0 ">
              <NavLink
                to={
                  location.pathname === '/appointments'
                    ? '/settings'
                    : '/setting'
                }
                className="flex flex-col items-center justify-center"
                style={{ textDecoration: 'none' }}
              >
                <div className="menu-text text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl">
                  <SettingOutlined
                    className={` ${
                      location.pathname === '/settings' ||
                      location.pathname === '/setting'
                        ? 'active-icon mb-2'
                        : 'mb-4'
                    }`}
                    onClick={nullSelectedRecord}
                  />
                  {location.pathname === '/appointments' ||
                  location.pathname === '/settings'
                    ? 'Settings'
                    : 'Customize'}
                </div>
              </NavLink>
            </li>
          </ul>
          <div className="flex flex-col gap-3 justify-center fixed bottom-24 left-4 z-[101]">
            {showProTrialCountDown && <ProTrialCountDown mobile />}
            {hasInviteUserAccess &&
              (showInviteUserWithMirrorOption ? (
                <InvitePractitioner mobile />
              ) : (
                <InviteUser mobile />
              ))}
          </div>
        </nav>
      ) : (
        <>
          {isLoading ? null : (
            <div
              className={`navbar-overlay fixed z-[99] w-full h-[100vh] bg-[rgba(0,0,0,0.5)] xl:hidden top-0 ${
                showSidebar ? 'left-0' : 'left-[-150%]'
              }`}
            ></div>
          )}

          <nav
            className={`side-navbar fixed z-[100] h-[100vh] top-0 ${
              showSidebar ? 'left-0' : 'left-[-150%]'
            } ${isMinimized ? 'minimized' : ''}`}
          >
            <CloseIcon
              className="absolute top-0 right-0 m-3 xl:hidden z-[101]"
              onClick={handleToggleSidebar}
            />

            <SideNavbarLogo
              isMinimized={isMinimized}
              setIsMinimized={setIsMinimized}
              setIsExpanding={setIsExpanding}
            />

            <ul
              className={`flex-grow ${
                isExpanding ? 'overflow-y-hidden' : 'overflow-y-auto'
              } overflow-x-hidden`}
            >
              {Array.isArray(menus) &&
                menus.length > 0 &&
                menus.map((route, index) => {
                  const {
                    path,
                    title,
                    icon,
                    submenu,
                    onClick,
                    showBadge,
                    className,
                    dataCelloClick,
                    celloClassName,
                  } = route;
                  const isMenuHovered = hoveredMenu === title;
                  const isActive = activeMenu === title;

                  const highlighted =
                    (isMenuHovered || isActive) &&
                    location.pathname.includes(path);

                  const menuContent = (
                    <WithBadge type={showBadge}>
                      <div
                        className={`icon-wrapper text-[1rem] ${
                          isMinimized ? 'minimized-icon' : ''
                        }`}
                      >
                        {React.cloneElement(icon, {
                          isFilled: highlighted,
                        })}
                      </div>
                      {!isMinimized && (
                        <Text
                          type={'span'}
                          className={`ml-2 text-[11.6px] flex items-center ${
                            celloClassName ? celloClassName : ''
                          } ${isMenuHovered || isActive ? '#00D091' : ''}`}
                          dataCelloClick={dataCelloClick}
                        >
                          {title}
                        </Text>
                      )}
                    </WithBadge>
                  );

                  const onClickHandler = () => {
                    handleMenuClick(title);
                    onClick?.();
                  };

                  const menuItem = path ? (
                    <NavLink
                      to={path}
                      className={`nav-link flex items-center ${
                        isActive && isSchedulePageActive && title !== 'Care AI'
                          ? 'actives'
                          : ''
                      } ${
                        title === 'Template Studio'
                          ? isMinimized
                            ? 'intercom-container minimized'
                            : 'intercom-container'
                          : ''
                      }`}
                      activeClassName="active"
                      onClick={onClickHandler}
                    >
                      {menuContent}
                    </NavLink>
                  ) : (
                    <div
                      className={`nav-link flex items-center cursor-pointer ${
                        isActive && isSchedulePageActive && 'active'
                      }`}
                      onClick={onClickHandler}
                    >
                      {menuContent}
                    </div>
                  );

                  return (
                    <li
                      key={index}
                      className={classNames(
                        `side-navbar_menu-list`,
                        {
                          active: isActive,
                        },
                        className
                      )}
                      onClick={
                        window.innerWidth < 1280
                          ? handleToggleSidebar
                          : () => null
                      }
                      onMouseEnter={() => handleMenuMouseEnter(title)}
                      onMouseLeave={handleMenuMouseLeave}
                    >
                      {isMinimized ? (
                        <Popover
                          content={
                            <SubMenuPopover
                              submenu={submenu}
                              visible={isMenuHovered}
                              title={title}
                            />
                          }
                          placement="right"
                          trigger="hover"
                          overlayClassName="submenu-popover-overlay"
                        >
                          {menuItem}
                        </Popover>
                      ) : (
                        <>
                          {menuItem}
                          {submenu && isActive && !isMinimized && (
                            <ul className="submenu ml-5">
                              {submenu.map((subItem, subIndex) => (
                                <li key={subIndex} className="submenu-item">
                                  <NavLink
                                    to={subItem.path}
                                    activeclassname="active"
                                    className="nav-link flex items-center"
                                  >
                                    {subItem.icon}
                                    <span
                                      className={`ml-2 ${
                                        isMenuHovered || isActive
                                          ? '#00D091'
                                          : ''
                                      }`}
                                    >
                                      <span className="text-[11.6px]">
                                        {subItem.title}
                                      </span>
                                    </span>
                                  </NavLink>
                                </li>
                              ))}
                            </ul>
                          )}
                        </>
                      )}
                    </li>
                  );
                })}
            </ul>
            {hasChromeExtensionLinkAccess && (
              <DownloadChromeExtensionSideBanner isMinimized={isMinimized} />
            )}
            <div className="flex flex-col items-center p-4 gap-3 shrink-0">
              {showProTrialCountDown && (
                <ProTrialCountDown isMinimized={isMinimized} />
              )}
              {hasInviteUserAccess &&
                (showInviteUserWithMirrorOption ? (
                  <InvitePractitioner isMinimized={isMinimized} />
                ) : (
                  <InviteUser isMinimized={isMinimized} />
                ))}
            </div>
          </nav>
        </>
      )}
    </>
  );
};
